import { useState } from 'react';

import { Icon } from '../../shared/Icon';
import { QAWebToolButton } from '../../shared/QAWebToolButton';
import { Timeline, TimelineItemData } from '../../shared/Timeline/Timeline';
import { TimelineHandle } from '../../shared/Timeline/TimelineHandle';
import { POST_TEMPLATES } from '../model/PostTemplates';
import { PortalPublication } from '../PortalPublication';
import { PostThumbnail } from '../PostThumbnail';
import { CreatePlannedPostModal } from './CreatePlannedPostModal';
import { SelectPostModalSingle } from './SelectPostModalSingle';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import { PortalPostState, TemplateId } from '@lifeserver/shared';

import { useCreatePortalPost } from '../../hooks/portal';
import { useSelectedLanguage } from '../../hooks/selectedLanugage';
import { usePreviewPortalPayload } from '../../hooks/usePreviewPortalPayload';
import { useQAWebTool } from '../../hooks/useQAWebTool';

import { handleError } from '../../utils/utils';

import styles from './PortalPublicationTimeline.module.scss';
import {
    getPublicationTranslationIssue,
    getPublicationTranslationIssueTitle,
} from './publicationTranslationWarnings';
import { useLocalizationsById } from './useLocalizationsById';

interface PortalPublicationTimelineProps {
    publications: PortalPublication[];
    onPublicationAdded?: (publication: PortalPublication) => void;
    onPublicationRemove?: (id: PortalPublication['id']) => void;
    onPublicationDuplicate?: (id: PortalPublication['id']) => void;
    onPublicationEdit?: (id: PortalPublication['id']) => void;
    onPublicationChanged?: (
        id: PortalPublication['id'],
        publication: Partial<PortalPublication>,
    ) => void;
    lanes: number;
    locked?: boolean;
    allowedTemplates?: TemplateId[];
}

const HEIGHT_PER_ROW = 60;

const POST_STATE_HANDLE_COLOR_MAP: Record<PortalPostState, string> = {
    todo: '#818181',
    in_progress: '#F0AD4E',
    in_review: '#2F77FF',
    ready: '#20882E',
};

const POST_STATE_BG_COLOR: Record<PortalPostState, string> = {
    todo: '#F1F3F3',
    in_progress: '#F0AD4E33', // 20% opacity
    in_review: '#2F77FF1A', // 10% opacity
    ready: '#FFFFFF',
};

export const PortalPublicationTimeline = ({
    publications,
    onPublicationChanged,
    onPublicationRemove,
    onPublicationDuplicate,
    onPublicationEdit,
    onPublicationAdded,
    lanes,
    locked,
    allowedTemplates,
}: PortalPublicationTimelineProps) => {
    const { mutate: createPost } = useCreatePortalPost();
    const { selectedLanguage } = useSelectedLanguage();
    const { localizationsById, isLoading: isLocalizationsLoading } =
        useLocalizationsById();

    const [isCreatingItem, setIsCreatingItem] =
        useState<TimelineItemData | null>(null);
    const [isCreatePlannedPostModalOpen, setIsCreatePlannedPostModalOpen] =
        useState(false);
    const webTool = useQAWebTool();
    const [livePreviewTime, setLivePreviewTime] = useState<Date>(new Date());
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    usePreviewPortalPayload(
        publications
            .filter(({ startTime, endTime }) => {
                return (
                    startTime.getTime() <= livePreviewTime.getTime() &&
                    (endTime === null ||
                        endTime.getTime() > livePreviewTime.getTime())
                );
            })
            .toSorted((a, b) => a.position - b.position)
            .map((publication) => publication.post.payload),
        { leading: false },
    );
    const handleCreate = (timelineItem: TimelineItemData) => {
        if (locked) {
            setSnackbarMessage(
                'This category is managed automatically and cannot be edited.',
            );
            setSnackbarOpen(true);
            return;
        }
        setIsCreatingItem(timelineItem);
    };

    const handleCreatePost = (
        postId: string,
        templateId: TemplateId,
        notes: string,
        tags: Array<{ name: string }>,
    ) => {
        if (!isCreatingItem) return;
        createPost(
            {
                createPostDto: {
                    id: postId,
                    templateId,
                    payload: POST_TEMPLATES[templateId].emptyData(),
                    state: 'todo',
                    notes: notes,
                    tags: tags,
                    minimumVersion: null,
                },
            },
            {
                onSuccess: (post) => {
                    onPublicationAdded?.({
                        startTime: isCreatingItem.start,
                        endTime: isCreatingItem.end,
                        position: isCreatingItem.lane,
                        id: crypto.randomUUID(),
                        post: post,
                    });
                    setIsCreatingItem(null);
                    setIsCreatePlannedPostModalOpen(false);
                },
                onError: (err) =>
                    handleError('Failed to create the planned post', err),
            },
        );
    };

    return (
        <>
            {isCreatingItem && (
                <SelectPostModalSingle
                    onPostSelected={(post) => {
                        onPublicationAdded?.({
                            startTime: isCreatingItem.start,
                            endTime: isCreatingItem.end,
                            position: isCreatingItem.lane,
                            id: crypto.randomUUID(),
                            post,
                        });
                        setIsCreatingItem(null);
                    }}
                    onClose={() => setIsCreatingItem(null)}
                    openNewPostModal={() =>
                        setIsCreatePlannedPostModalOpen(true)
                    }
                    allowedTemplates={allowedTemplates}
                />
            )}
            {isCreatingItem && (
                <CreatePlannedPostModal
                    open={isCreatePlannedPostModalOpen}
                    onPostCreate={handleCreatePost}
                    onClose={() => setIsCreatePlannedPostModalOpen(false)}
                    startDate={isCreatingItem?.start}
                    endDate={isCreatingItem?.end}
                    allowedTemplates={allowedTemplates}
                />
            )}
            <Timeline
                onCreate={(timelineItem) => {
                    handleCreate(timelineItem);
                }}
                onDelete={onPublicationRemove}
                onDuplicate={onPublicationDuplicate}
                onEdit={onPublicationEdit}
                laneHeight={HEIGHT_PER_ROW}
                lanes={lanes}
                className={styles.timeline}
                toolbarClassName={styles.timelineToolbar}
                timelineItems={publications.map((publication) => ({
                    id: publication.id,
                    lane: publication.position,
                    start: new Date(publication.startTime),
                    end: publication.endTime,
                    onChange: (item) => {
                        onPublicationChanged?.(publication.id, {
                            ...publication,
                            startTime: item.start,
                            endTime: item.end,
                            position: item.lane,
                        });
                    },
                    handleColor:
                        POST_STATE_HANDLE_COLOR_MAP[publication.post.state],
                    render: () => {
                        const hasVariantTag =
                            publication.post.postTags?.includes('variant');
                        const translationIssue =
                            isLocalizationsLoading || !localizationsById
                                ? null
                                : getPublicationTranslationIssue(
                                      publication,
                                      selectedLanguage,
                                      localizationsById,
                                  );
                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    overflow: 'hidden',
                                    alignItems: 'center',
                                    flex: 1,
                                    paddingLeft: 10,
                                    gap: 10,
                                    borderRadius: 8,
                                    backgroundColor:
                                        POST_STATE_BG_COLOR[
                                            publication.post.state
                                        ],
                                    opacity: hasVariantTag ? 0.6 : 1,
                                }}
                            >
                                <PostThumbnail
                                    postData={publication.post.payload}
                                    templateId={publication.post.templateId}
                                    height={HEIGHT_PER_ROW - 20}
                                    zoomOnHover={false}
                                />
                                <p
                                    style={{
                                        fontWeight: 'bold',
                                        opacity:
                                            publication.post.state === 'todo'
                                                ? 0.6
                                                : 1,
                                    }}
                                >
                                    {publication.post.id}
                                </p>
                                {translationIssue && (
                                    <Tooltip
                                        title={getPublicationTranslationIssueTitle(
                                            translationIssue,
                                        )}
                                    >
                                        <span
                                            className={
                                                styles.translationWarning
                                            }
                                            tabIndex={0}
                                            aria-label={getPublicationTranslationIssueTitle(
                                                translationIssue,
                                            )}
                                        >
                                            <Icon name="warning" />
                                        </span>
                                    </Tooltip>
                                )}
                                {hasVariantTag && (
                                    <span className={styles.variantBadge}>
                                        Variant
                                    </span>
                                )}
                            </div>
                        );
                    },
                }))}
                toolbarChildren={
                    <QAWebToolButton className={styles.toolbarPreviewButton} />
                }
            >
                {webTool.socketState === 'connected' && (
                    <TimelineHandle
                        time={livePreviewTime}
                        onChange={setLivePreviewTime}
                    />
                )}
            </Timeline>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                slotProps={{
                    content: {
                        className: styles.errorSnackbar,
                    },
                }}
            />
        </>
    );
};
